<template>
	<validation-observer
		v-loading="formProcessing"
		v-slot="{ invalid }"
		@submit.prevent="onSubmit"
		ref="form"
		tag="form"
		class="form mx-auto"
	>
		<searchable-dropdown
			ref="tpa"
			v-model="models.tpa"
			@input="getParameters('umr')"
			label="TPA"
			name="TPA"
			:is-loading="'tpa' === fetchingOption"
			:disabled="tpaExists"
			:options="options.tpa"
			multiple
		></searchable-dropdown>

		<searchable-dropdown
			ref="umr"
			v-model="models.umr"
			@input="getParameters('period')"
			label="UMR"
			name="UMR"
			:is-loading="'umr' === fetchingOption"
			:disabled="!options.umr.length"
			:options="options.umr"
			multiple
		></searchable-dropdown>

		<searchable-dropdown
			ref="coverHolder"
			v-model="models.cover_holder"
			label="Cover Holder"
			name="Cover Holder"
			:is-loading="'coverHolder' === fetchingOption"
			:disabled="!options.coverHolder.length"
			:options="options.coverHolder"
			multiple
		></searchable-dropdown>

		<searchable-dropdown
			ref="period"
			v-model="models.period"
			label="Report Date"
			name="Report Date"
			:is-loading="'period' === fetchingOption"
			:disabled="!options.period.length"
			:options="options.period"
			multiple
		></searchable-dropdown>

		<div class="form-group m-0">
			<label>Flag</label>
			<grouped-options
				v-model="models.flag"
				name="flag"
				native-type="checkbox"
				button-classes="badge badge-pill mr-2 text-capitalize"
				class="button-group-toggle--flag"
				:options="options.flag"
			>
				<template #extra>
					<button
						@click.prevent="selectAllFlags"
						:class="{
							active:
								models.flag &&
								models.flag.length === options.flag.length,
						}"
						class="btn badge badge-pill mr-2 text-capitalize"
					>
						All
					</button>
				</template>
			</grouped-options>
		</div>

		<div class="mt-4 d-flex align-items-center justify-content-between">
			<div class="form-group m-0">
				<label>Export by</label>
				<grouped-options
					v-model="models.exportBy"
					name="export_by"
					button-classes="badge badge-pill mr-2 text-capitalize"
					:options="options.exportBy"
				></grouped-options>
			</div>

			<div>
				<a @click.prevent="clearParameters" class="mr-3" href>Clear</a>
				<base-button
					:disabled="invalid || (fetchingOption ? true : false)"
					:loading="formProcessing"
					native-type="submit"
					type="primary"
					size="md"
					class="py-3"
					>Export</base-button
				>
			</div>
		</div>
	</validation-observer>
</template>

<script>
import moment from "moment";
import { userMixin } from "@mixins";
import SearchableDropdown from "@components/SearchableDropdown";
import GroupedOptions from "@components/GroupedOptions";

export default {
	name: "form-export-parameters",
	inject: ["reportSvc"],
	mixins: [userMixin],
	components: {
		[SearchableDropdown.name]: SearchableDropdown,
		[GroupedOptions.name]: GroupedOptions,
	},
	props: {
		powerbiReportId: {
			type: String,
			required: true,
		},
	},
	data() {
		return {
			formProcessing: false,
			tpaExists: false,
			fetchingOption: undefined,
			models: {},
			options: {
				tpa: [],
				umr: [],
				coverHolder: [],
				period: [],
				flag: [
					{ value: "1", label: "Red" },
					{ value: "2", label: "Yellow" },
					{ value: "3", label: "Green" },
				],
				exportBy: [
					{ value: "tpa", label: "TPA" },
					{ value: "umr", label: "UMR" },
				],
			},
		};
	},
	methods: {
		async getUserTPA() {
			if (!this.currentUser.company) return false;

			if ("TPA" !== this.currentUser.company.role.role) return false;

			const idx = this.options.tpa.findIndex(
				(item) => item.text == this.currentUser.company.role_value
			);

			if (0 <= idx) {
				this.tpaExists = true;
				this.models.tpa = [this.options.tpa[idx].value];

				this.$refs.tpa.onSelect(this.options.tpa[idx].value);

				this.getParameters("umr");
			}
		},
		async getParameters(type = "tpa") {
			const parameters = {
				role: this.companyRole,
				role_value: this.companyRoleValue,
			};
			const TPA = this.models.tpa || "";
			const UMR = this.models.umr ? this.models.umr.join(",") : "";

			if ("umr" === type) {
				if (TPA) {
					parameters.TPA_Name = TPA;

					this.models.umr = undefined;
					this.models.period = undefined;

					this.options.period = [];

					this.$refs.form.reset();
				} else {
					this.options.umr = [];
					this.options.period = [];

					return false;
				}
			} else if ("period" === type) {
				if (TPA && UMR) {
					parameters.TPA_Name = TPA;
					parameters.Unique_Market_Reference_UMR = UMR;
				} else {
					this.options.period = [];

					return false;
				}
			}

			this.fetchingOption = type;

			const rs = await this.reportSvc.getExportParameters(parameters);

			this.fetchingOption = undefined;
			this.options[type] = rs.data.data.map((item) => {
				const option =
					"period" === type
						? moment(item).format("YYYY-MM-DD")
						: item;

				return {
					text: option,
					value: option,
				};
			});
		},
		async onSubmit() {
			let parameters = [];

			this.formProcessing = true;

			if (this.models.tpa)
				parameters = parameters.concat(
					this.models.tpa.map((item) => {
						return {
							name: "TPA",
							value: item,
						};
					})
				);

			if (this.models.umr)
				parameters.push({
					name: "Unique_Market_Reference_UMR",
					value: this.models.umr.join(","),
				});

			if (this.models.period)
				parameters.push({
					name: "Reporting_Period_End_Date",
					value: this.models.period.join(","),
				});

			await this.reportSvc.exportReport(
				this.powerbiReportId,
				parameters,
				this.models.exportBy,
				this.reportSvc.report.powerbi_role_value
			);

			this.$toasted.show(
				"Your export is in progress. You will be emailed when it's ready on the Downloads page"
			);

			this.$router.push({ name: "PreviousReports" });
		},
		clearParameters() {
			this.models = {};
		},
		selectAllFlags() {
			const flags = this.options.flag.map((item) => item.value);

			this.$set(
				this.models,
				"flag",
				this.models.flag && flags.length === this.models.flag.length
					? []
					: flags
			);
		},
	},
	async mounted() {
		await this.getParameters();
		await this.getUserTPA();

		this.$bus.trackEvent("error-catched", () => {
			this.formProcessing = false;
		});
	},
};
</script>

<style lang="scss">
.button-group-toggle {
	&--flag {
		.badge {
			&.active {
				&:nth-child(2) {
					background-color: #f5365c;
				}

				&:nth-child(3) {
					color: #000;
					background-color: #fcf93e;
				}

				&:nth-child(4) {
					background-color: #48b900;
				}
			}
		}
	}
}
</style>
